import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ClipboardList, X, Minus, Plus, Trash2, MessageCircle, Mail, Download, Share2, Check } from "lucide-react";
import { useEnquiry } from "../context/EnquiryContext";
import { buildWhatsAppUrl, buildMailto, selectionLink, downloadCataloguePdf, inr, WARRANTY_NOTE } from "../lib/catalogue";

const HIDE = /^\/(admin|warehouse|godown|poster)(\/|$)/;

export default function EnquiryBar() {
  const { pathname } = useLocation();
  const { items, remove, setQty, clear, count } = useEnquiry();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  if (HIDE.test(pathname) || pathname === "/stock/add") return null;
  if (count === 0 && !open) return null;

  const shareList = async () => {
    const url = selectionLink(items);
    try {
      if (navigator.share) { await navigator.share({ title: "Kapatia enquiry list", url }); return; }
      await navigator.clipboard.writeText(url);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch (e) { console.debug("share list failed", e); }
  };

  const total = items.reduce((s, i) => s + (i.price_on_request ? 0 : i.price * i.qty), 0);

  return (
    <>
      {/* Floating pill — bottom-left to avoid the WhatsApp FAB / mobile CTA bar */}
      {count > 0 && !open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="enquiry-list-open"
          className="kp-button fixed bottom-4 left-4 z-[60] inline-flex items-center gap-2 rounded-full px-5 py-3 font-heading text-[14px] font-bold text-white shadow-lg transition-transform hover:-translate-y-0.5 sm:bottom-5 sm:left-5"
          style={{ background: "linear-gradient(135deg, var(--orange-500), #ff3f1f)" }}
        >
          <ClipboardList size={18} /> Enquiry list
          <span className="grid h-6 min-w-6 place-items-center rounded-full bg-white px-1.5 text-[12px] font-extrabold" style={{ color: "var(--orange-700)" }} data-testid="enquiry-list-count">{count}</span>
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[70]" role="dialog" aria-label="Enquiry list" data-testid="enquiry-list-drawer">
          <div className="absolute inset-0" style={{ background: "rgba(13,30,24,0.45)" }} onClick={() => setOpen(false)} />
          <aside className="absolute right-0 top-0 flex h-full w-full max-w-[440px] flex-col bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-4" style={{ borderColor: "var(--line)" }}>
              <div>
                <h2 className="font-heading text-[18px] font-extrabold" style={{ color: "var(--green-950)" }}>Your enquiry list</h2>
                <p className="text-[12px]" style={{ color: "var(--muted)" }}>{count} item{count === 1 ? "" : "s"} selected</p>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close" data-testid="enquiry-list-close"
                      className="grid h-9 w-9 place-items-center rounded-full border" style={{ borderColor: "var(--line)" }}>
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {count === 0 ? (
                <div className="grid place-items-center gap-2 py-16 text-center">
                  <ClipboardList size={36} style={{ color: "var(--muted)" }} />
                  <p className="font-heading font-bold" style={{ color: "var(--green-950)" }}>No items yet</p>
                  <p className="text-[13px]" style={{ color: "var(--muted)" }}>Add products from the store to build an enquiry.</p>
                </div>
              ) : (
                <ul className="flex flex-col gap-3">
                  {items.map((i) => (
                    <li key={i.id} className="rounded-xl border p-3" style={{ borderColor: "var(--line)" }} data-testid={`enquiry-row-${i.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          {i.code && <span className="inline-block rounded px-1.5 py-0.5 text-[10.5px] font-bold" style={{ background: "var(--green-100)", color: "var(--green-900)" }}>{i.code}</span>}
                          <Link to={`/product/${i.id}`} onClick={() => setOpen(false)} className="mt-1 block font-sans text-[13.5px] font-medium leading-snug hover:underline" style={{ color: "var(--ink)" }}>{i.name}</Link>
                          <span className="mt-0.5 block font-heading text-[13px] font-extrabold" style={{ color: "var(--orange-700)" }}>{i.price_on_request ? "Price on request" : inr(i.price)}</span>
                        </div>
                        <button onClick={() => remove(i.id)} aria-label="Remove" data-testid={`enquiry-remove-${i.id}`}
                                className="grid h-8 w-8 shrink-0 place-items-center rounded-full border" style={{ borderColor: "var(--line)", color: "var(--orange-700)" }}>
                          <Trash2 size={15} />
                        </button>
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-[12px] font-semibold" style={{ color: "var(--muted)" }}>Qty</span>
                        <div className="inline-flex items-center rounded-full border" style={{ borderColor: "var(--line)" }}>
                          <button onClick={() => setQty(i.id, i.qty - 1)} aria-label="Decrease" data-testid={`enquiry-qty-dec-${i.id}`} className="grid h-8 w-8 place-items-center" style={{ color: "var(--green-900)" }}><Minus size={14} /></button>
                          <span className="min-w-8 text-center font-heading text-[13px] font-bold" data-testid={`enquiry-qty-${i.id}`}>{i.qty}</span>
                          <button onClick={() => setQty(i.id, i.qty + 1)} aria-label="Increase" data-testid={`enquiry-qty-inc-${i.id}`} className="grid h-8 w-8 place-items-center" style={{ color: "var(--green-900)" }}><Plus size={14} /></button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {count > 0 && (
              <div className="border-t px-5 py-4" style={{ borderColor: "var(--line)" }}>
                <p className="mb-2 text-[11.5px]" style={{ color: "var(--muted)" }}>{WARRANTY_NOTE}</p>
                {total > 0 && (
                  <p className="mb-3 flex items-center justify-between font-heading text-[13px] font-bold" style={{ color: "var(--green-950)" }}>
                    <span>Indicative total (priced items)</span><span>{inr(total)}</span>
                  </p>
                )}
                <div className="grid grid-cols-2 gap-2.5">
                  <a href={buildWhatsAppUrl(items)} target="_blank" rel="noopener noreferrer" data-testid="enquiry-list-whatsapp"
                     data-item-codes={items.map((i) => i.code).filter(Boolean).join(",")} data-item-count={items.length}
                     className="kp-button col-span-2 inline-flex items-center justify-center gap-2 rounded-full px-4 py-3 font-heading text-[14px] font-bold text-white transition-transform hover:-translate-y-0.5"
                     style={{ background: "linear-gradient(135deg, var(--bright-green), var(--bright-green-strong))" }}>
                    <MessageCircle size={16} /> Enquire on WhatsApp
                  </a>
                  <a href={buildMailto(items)} data-testid="enquiry-list-email"
                     data-item-codes={items.map((i) => i.code).filter(Boolean).join(",")} data-item-count={items.length}
                     className="kp-button inline-flex items-center justify-center gap-2 rounded-full border bg-white px-4 py-2.5 font-heading text-[13.5px] font-bold transition-transform hover:-translate-y-0.5"
                     style={{ borderColor: "var(--line)", color: "var(--green-900)" }}>
                    <Mail size={15} /> Email
                  </a>
                  <button onClick={() => downloadCataloguePdf({ title: "Selected items", items })} data-testid="enquiry-list-download"
                          className="kp-button inline-flex items-center justify-center gap-2 rounded-full border bg-white px-4 py-2.5 font-heading text-[13.5px] font-bold transition-transform hover:-translate-y-0.5"
                          style={{ borderColor: "var(--line)", color: "var(--green-900)" }}>
                    <Download size={15} /> PDF
                  </button>
                  <button onClick={shareList} data-testid="enquiry-list-share"
                          className="kp-button inline-flex items-center justify-center gap-2 rounded-full border bg-white px-4 py-2.5 font-heading text-[13.5px] font-bold transition-transform hover:-translate-y-0.5"
                          style={{ borderColor: "var(--line)", color: "var(--green-900)" }}>
                    {copied ? <Check size={15} style={{ color: "var(--green-700)" }} /> : <Share2 size={15} />} {copied ? "Copied" : "Share link"}
                  </button>
                  <button onClick={clear} data-testid="enquiry-list-clear"
                          className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 font-heading text-[13px] font-bold"
                          style={{ color: "var(--orange-700)" }}>
                    Clear all
                  </button>
                </div>
              </div>
            )}
          </aside>
        </div>
      )}
    </>
  );
}
